import { createContext, useContext, useEffect, useRef } from 'react';
import { AffixManagerStore, CONTENT_PADDING } from './AffixManagerStore.ts';

const AffixManagerStoreContext = createContext<AffixManagerStore | null>(null);

export interface IAffixManagerProviderProps {
  children: React.ReactNode;
}

/**
 * Provider per il sistema AffixManager.
 *
 * Crea e fornisce lo store condiviso che permette ai componenti `ManagedAffix`
 * di registrare le proprie altezze e calcolare automaticamente gli offset.
 *
 * Aggiorna le variabili CSS `--header-height` e `--header-height-value` su `:root`
 * con l'altezza totale degli affix registrati.
 *
 * Imposta inoltre `scroll-padding-top` su `:root` così che gli scroll-into-view
 * (anchor `#id`, salto nativo su hash, scroll da `focus()`) non nascondano il
 * target sotto gli affix. Gli scroll a coordinate esplicite (es. `targetOffset`
 * dell'Anchor antd) non sono influenzati.
 *
 * Va posizionato nel layout principale di ogni app, wrappando header e contenuto.
 */
export function AffixManagerProvider({ children }: IAffixManagerProviderProps) {
  const storeRef = useRef<AffixManagerStore>();
  if (!storeRef.current) {
    storeRef.current = new AffixManagerStore();
  }
  const store = storeRef.current;

  // Aggiorna le variabili CSS quando il totale cambia
  useEffect(() => {
    const root = document.documentElement.style;
    root.setProperty('--content-padding', `${CONTENT_PADDING}px`);
    root.setProperty(
      'scroll-padding-top',
      'calc(var(--header-height, 0px) + var(--content-padding, 24px))',
    );
    const subscription = store.snapshot$.subscribe(snapshot => {
      root.setProperty('--header-height', `${snapshot.totalHeight}px`);
      root.setProperty('--header-height-value', `${snapshot.totalHeight}`);
    });
    return () => {
      subscription.unsubscribe();
      root.removeProperty('--header-height');
      root.removeProperty('--header-height-value');
      root.removeProperty('--content-padding');
      root.removeProperty('scroll-padding-top');
    };
  }, [store]);

  return (
    <AffixManagerStoreContext.Provider value={store}>
      {children}
    </AffixManagerStoreContext.Provider>
  );
}

/**
 * Accede allo store AffixManager dal context.
 * Ritorna `null` se nessun `AffixManagerProvider` è nel tree (graceful degradation).
 */
export function useAffixManagerStore(): AffixManagerStore | null {
  return useContext(AffixManagerStoreContext);
}
